import React from "react";
import { Button, Dropdown, Space, Switch, Table, Tag, Tooltip } from "antd";
import {
  DeleteOutlined,
  EditOutlined,
  LinkOutlined,
  MoreOutlined,
  ReloadOutlined,
  UnorderedListOutlined,
} from "@ant-design/icons";
import type { MenuProps } from "antd";
import type { ColumnsType } from "antd/es/table";

import { humanReadableDate } from "../../../../helper/dateTimeHelper";
import { INTEGRATION_STATUS, providerLabels } from "../constants";

import type { WebhookIntegration } from "../types";

interface WebhookIntegrationsTableProps {
  integrations: WebhookIntegration[];
  isLoading: boolean;
  canEdit: boolean;
  canDelete: boolean;
  togglingId: number | null;
  onViewUrl: (integration: WebhookIntegration) => void;
  onViewEvents: (integration: WebhookIntegration) => void;
  onEdit: (integration: WebhookIntegration) => void;
  onToggleStatus: (integration: WebhookIntegration, isActive: boolean) => void;
  onRegenerate: (integration: WebhookIntegration) => void;
  onDelete: (integration: WebhookIntegration) => void;
}

const ACTION_KEY = {
  VIEW_URL: "view-url",
  VIEW_EVENTS: "view-events",
  EDIT: "edit",
  REGENERATE: "regenerate",
  DELETE: "delete",
} as const;

export const WebhookIntegrationsTable: React.FC<WebhookIntegrationsTableProps> = ({
  integrations,
  isLoading,
  canEdit,
  canDelete,
  togglingId,
  onViewUrl,
  onViewEvents,
  onEdit,
  onToggleStatus,
  onRegenerate,
  onDelete,
}) => {
  const buildActionItems = (): MenuProps["items"] => {
    const items: MenuProps["items"] = [
      { key: ACTION_KEY.VIEW_URL, icon: <LinkOutlined />, label: "View webhook URL & setup" },
      { key: ACTION_KEY.VIEW_EVENTS, icon: <UnorderedListOutlined />, label: "View recent events" },
    ];
    if (canEdit) {
      items.push(
        { type: "divider" },
        { key: ACTION_KEY.EDIT, icon: <EditOutlined />, label: "Edit connection" },
        { key: ACTION_KEY.REGENERATE, icon: <ReloadOutlined />, label: "Regenerate URL & secret" },
      );
    }
    if (canDelete) {
      items.push(
        { type: "divider" },
        { key: ACTION_KEY.DELETE, icon: <DeleteOutlined />, label: "Delete connection", danger: true },
      );
    }
    return items;
  };

  const handleAction = (key: string, record: WebhookIntegration) => {
    if (key === ACTION_KEY.VIEW_URL) onViewUrl(record);
    if (key === ACTION_KEY.VIEW_EVENTS) onViewEvents(record);
    if (key === ACTION_KEY.EDIT) onEdit(record);
    if (key === ACTION_KEY.REGENERATE) onRegenerate(record);
    if (key === ACTION_KEY.DELETE) onDelete(record);
  };

  const columns: ColumnsType<WebhookIntegration> = [
    {
      title: "Connection",
      dataIndex: "name",
      key: "name",
      render: (name: string, record) => (
        <div>
          <div className="fw-semibold">{name}</div>
          <Space size={4} className="mt-1">
            <Tag color="green">
              <i className="ti ti-brand-whatsapp me-1" />
              WhatsApp
            </Tag>
            <Tag>{providerLabels[record.provider] || record.provider}</Tag>
            {record.secret_configured ? (
              <Tag color="blue">
                <i className="ti ti-lock me-1" />
                Secured
              </Tag>
            ) : (
              <Tooltip title="This connection has no secret and rejects all events. Use Regenerate URL & secret to secure it.">
                <Tag color="red">No secret</Tag>
              </Tooltip>
            )}
          </Space>
        </div>
      ),
    },
    {
      title: "Business Number / WABA",
      key: "account",
      render: (_, record) => (
        <div className="small">
          <div>{record.business_phone ? `+${record.business_phone}` : "—"}</div>
          <div className="text-muted">{record.waba_id || "WABA not detected yet"}</div>
        </div>
      ),
    },
    {
      title: "Leads / Chats",
      key: "counts",
      width: 130,
      render: (_, record) => (
        <span>
          <span className="fw-semibold">{record.lead_count}</span>
          <span className="text-muted"> / {record.conversation_count}</span>
        </span>
      ),
    },
    {
      title: "Last Event",
      dataIndex: "last_event_at",
      key: "last_event_at",
      width: 190,
      render: (value: string | null) =>
        value ? humanReadableDate(value, true) : <span className="text-muted">No events yet</span>,
    },
    {
      title: "Active",
      dataIndex: "status",
      key: "status",
      width: 90,
      render: (status: number, record) => (
        <Switch
          checked={status === INTEGRATION_STATUS.ACTIVE}
          disabled={!canEdit}
          loading={togglingId === record.id}
          onChange={(checked) => onToggleStatus(record, checked)}
        />
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 90,
      align: "center",
      fixed: "right",
      render: (_, record) => (
        <Dropdown
          trigger={["click"]}
          placement="bottomRight"
          menu={{
            items: buildActionItems(),
            onClick: ({ key, domEvent }) => {
              domEvent.stopPropagation();
              handleAction(key, record);
            },
          }}>
          <Button
            size="small"
            icon={<MoreOutlined />}
            aria-label={`Actions for ${record.name}`}
          />
        </Dropdown>
      ),
    },
  ];

  return (
    <Table<WebhookIntegration>
      rowKey="id"
      columns={columns}
      dataSource={integrations}
      loading={isLoading}
      pagination={false}
      scroll={{ x: 1000 }}
      locale={{ emptyText: "No webhook connections yet. Click “Connect New” to add one." }}
    />
  );
};
